/**
 * Billing, warehouse exports, SCIM, license, and Teams bot API clients.
 * Billing shapes come from packages/protocol/src/api-billing.ts. Exports and
 * Teams routes are served by services/internal/{warehouse,teamsbot}. SCIM and
 * license endpoints are the contract expected from the SCIM/billing backends
 * (see README "API expectations").
 */
import type { Caps, Usage } from "@backstory/protocol";
import { request } from "../../api";

export type { Caps, Usage };

const enc = encodeURIComponent;

// ---------------- billing ----------------

export const billingApi = {
  usage: (org: string, month?: string) => request<Usage>(`/v1/orgs/${enc(org)}/usage${month ? `?month=${enc(month)}` : ""}`),
  patchCaps: (org: string, caps: Caps) => request<Caps>(`/v1/orgs/${enc(org)}/billing/caps`, { method: "PATCH", body: caps }),
};

// ---------------- warehouse exports ----------------

export type DestinationType = "s3" | "snowflake" | "bigquery";

export interface Destination {
  id: string;
  orgId: string;
  type: DestinationType;
  name: string;
  enabled: boolean;
  phiCovered: boolean;
  config: Record<string, unknown>;
  secretsSet: string[];
  createdAt: string;
  lastRunAt?: string;
  lastError?: string;
}

export interface DestinationInput {
  type: DestinationType;
  name: string;
  enabled?: boolean;
  phiCovered: boolean;
  config: Record<string, unknown>;
  secrets: Record<string, string>;
}

export interface ExportRun {
  id: string;
  orgId: string;
  destinationId?: string;
  trigger: "nightly" | "manual";
  status: "queued" | "running" | "done" | "failed";
  startedAt?: string;
  finishedAt?: string;
  windowFrom?: string;
  windowTo?: string;
  rows: number;
  bytes: number;
  files: number;
  tables: Record<string, { rows: number; bytes: number; files: number }>;
  error?: string;
  createdAt: string;
}

export const exportsApi = {
  destinations: (org: string) => request<{ items: Destination[] }>(`/v1/orgs/${enc(org)}/exports/destinations`).then((r) => r.items),
  createDestination: (org: string, body: DestinationInput) => request<Destination>(`/v1/orgs/${enc(org)}/exports/destinations`, { method: "POST", body }),
  deleteDestination: (org: string, id: string) => request<void>(`/v1/orgs/${enc(org)}/exports/destinations/${enc(id)}`, { method: "DELETE" }),
  run: (org: string, destinationId?: string) => request<ExportRun>(`/v1/orgs/${enc(org)}/exports/run`, { method: "POST", body: destinationId ? { destinationId } : {} }),
  runs: (org: string) => request<{ items: ExportRun[] }>(`/v1/orgs/${enc(org)}/exports/runs?limit=50`).then((r) => r.items),
};

// ---------------- SCIM ----------------

export interface ScimToken {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
}

export interface ScimTokenCreated extends ScimToken {
  token: string;
  baseUrl: string;
}

export interface ScimGroupRoles {
  roles: Record<string, string>;
  defaultRole: string;
}

export const scimApi = {
  tokens: (org: string) => request<ScimToken[]>(`/v1/orgs/${enc(org)}/scim/tokens`),
  groupRoles: (org: string) => request<ScimGroupRoles>(`/v1/orgs/${enc(org)}/scim/group-roles`),
  putGroupRoles: (org: string, roles: Record<string, string>) => request<ScimGroupRoles>(`/v1/orgs/${enc(org)}/scim/group-roles`, { method: "PUT", body: { roles } }),
  createToken: (org: string, name = "SCIM") => request<ScimTokenCreated>(`/v1/orgs/${enc(org)}/scim/token`, { method: "POST", body: { name } }),
  revokeToken: (org: string, id: string) => request<void>(`/v1/orgs/${enc(org)}/scim/token/${enc(id)}`, { method: "DELETE" }),
  rotateToken: (org: string, id: string) => request<ScimTokenCreated>(`/v1/orgs/${enc(org)}/scim/token/${enc(id)}/rotate`, { method: "POST", body: {} }),
};

// ---------------- Teams bot ----------------

export interface TeamsInstallation {
  id: string;
  orgId?: string;
  msTenantId: string;
  teamName?: string;
  channelName?: string;
  conversationType: string;
  minSeverity: string;
  enabled: boolean;
  installedAt: string;
  linkedAt?: string;
}

export const teamsApi = {
  installations: (org: string) => request<{ items: TeamsInstallation[]; configured: boolean }>(`/v1/orgs/${enc(org)}/teams/installations`),
  link: (org: string, code: string) => request<TeamsInstallation>(`/v1/orgs/${enc(org)}/teams/link`, { method: "POST", body: { code } }),
  patch: (org: string, id: string, body: { minSeverity?: string; enabled?: boolean }) => request<TeamsInstallation>(`/v1/orgs/${enc(org)}/teams/installations/${enc(id)}`, { method: "PATCH", body }),
  remove: (org: string, id: string) => request<void>(`/v1/orgs/${enc(org)}/teams/installations/${enc(id)}`, { method: "DELETE" }),
};

export const billingKeys = {
  usage: (o: string) => ["billing", "usage", o] as const,
  limits: (o: string) => ["billing", "limits", o] as const,
  state: (o: string) => ["billing", "state", o] as const,
  invoices: (o: string) => ["billing", "invoices", o] as const,
  payments: (o: string) => ["billing", "payments", o] as const,
  aiCreditWallet: (o: string) => ["billing", "ai-credit-wallet", o] as const,
  license: ["billing", "license"] as const,
  destinations: (o: string) => ["exports", "destinations", o] as const,
  runs: (o: string) => ["exports", "runs", o] as const,
  scimTokens: (o: string) => ["scim", "tokens", o] as const,
  scimRoles: (o: string) => ["scim", "roles", o] as const,
  scimLog: (o: string) => ["scim", "log", o] as const,
  teams: (o: string) => ["teams", o] as const,
};

/** Fields per destination type: config keys and secret keys with labels. */
export const DESTINATION_FIELDS: Record<DestinationType, { label: string; hint: string; config: Array<{ key: string; label: string; required?: boolean; placeholder?: string }>; secrets: Array<{ key: string; label: string; required?: boolean; multiline?: boolean }> }> = {
  s3: {
    label: "Amazon S3 / S3-compatible",
    hint: "Parquet files are copied under the prefix using the same table/dt= layout.",
    config: [
      { key: "bucket", label: "Bucket", required: true, placeholder: "acme-analytics" },
      { key: "prefix", label: "Prefix", placeholder: "backstory" },
      { key: "region", label: "Region", placeholder: "us-east-1" },
      { key: "endpoint", label: "Endpoint (S3-compatible only)", placeholder: "https://s3.example.com" },
      { key: "roleArn", label: "Assume role ARN (instead of keys)", placeholder: "arn:aws:iam::123456789012:role/backstory-export" },
      { key: "externalId", label: "External id (for the role)", placeholder: "" },
    ],
    secrets: [
      { key: "accessKeyId", label: "Access key id" },
      { key: "secretAccessKey", label: "Secret access key" },
    ],
  },
  snowflake: {
    label: "Snowflake",
    hint: "Files are staged in your S3 bucket and loaded with COPY INTO through the SQL REST API using key-pair authentication.",
    config: [
      { key: "account", label: "Account identifier", required: true, placeholder: "acme-xy12345" },
      { key: "user", label: "User", required: true, placeholder: "BACKSTORY_LOADER" },
      { key: "database", label: "Database", required: true },
      { key: "schema", label: "Schema", required: true, placeholder: "PUBLIC" },
      { key: "warehouse", label: "Warehouse", required: true },
      { key: "role", label: "Role" },
      { key: "stageBucket", label: "Stage bucket (S3)", required: true },
      { key: "stagePrefix", label: "Stage prefix" },
      { key: "tablePrefix", label: "Table prefix", placeholder: "backstory_" },
    ],
    secrets: [
      { key: "privateKeyPem", label: "Private key (PKCS#8 PEM)", required: true, multiline: true },
      { key: "accessKeyId", label: "Stage bucket access key id" },
      { key: "secretAccessKey", label: "Stage bucket secret access key" },
    ],
  },
  bigquery: {
    label: "Google BigQuery",
    hint: "Files are written to Cloud Storage with HMAC interoperability keys and loaded with a load job per table.",
    config: [
      { key: "project", label: "GCP project", required: true },
      { key: "dataset", label: "Dataset", required: true },
      { key: "gcsBucket", label: "Cloud Storage bucket", required: true },
      { key: "gcsPrefix", label: "Bucket prefix" },
      { key: "location", label: "Location", placeholder: "US" },
      { key: "tablePrefix", label: "Table prefix", placeholder: "backstory_" },
    ],
    secrets: [
      { key: "serviceAccountJson", label: "Service account JSON", required: true, multiline: true },
      { key: "hmacKey", label: "HMAC access id", required: true },
      { key: "hmacSecret", label: "HMAC secret", required: true },
    ],
  },
};

export function validateDestination(d: DestinationInput): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!d.name.trim()) errors.name = "Name the destination.";
  const spec = DESTINATION_FIELDS[d.type];
  for (const f of spec.config) {
    if (f.required && !String(d.config[f.key] ?? "").trim()) errors[f.key] = `${f.label} is required.`;
  }
  for (const s of spec.secrets) {
    if (s.required && !(d.secrets[s.key] ?? "").trim()) errors[s.key] = `${s.label} is required.`;
  }
  if (d.type === "s3" && !d.secrets.accessKeyId && !String(d.config.roleArn ?? "").trim()) errors.accessKeyId = "Provide access keys or a role ARN.";
  if (d.type === "bigquery" && d.secrets.serviceAccountJson) {
    try {
      const sa = JSON.parse(d.secrets.serviceAccountJson) as { client_email?: string; private_key?: string };
      if (!sa.client_email || !sa.private_key) errors.serviceAccountJson = "Service account JSON needs client_email and private_key.";
    } catch {
      errors.serviceAccountJson = "Service account JSON is not valid JSON.";
    }
  }
  return errors;
}
